import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead } from "@/components/SeoHead";

const VERSION = (process.env.REACT_APP_FORM_VERSION || "v3").toLowerCase();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "sema_demo_form";

const TEAM_SIZES = ["Just me", "2–10", "11–50", "51–200", "201–1000", "1000+"];
const ROLES = ["Data / Analytics", "Engineering", "Product", "Leadership / Exec", "Operations", "Other"];
const WAREHOUSES = ["Postgres", "Snowflake", "BigQuery", "Databricks", "Redshift", "MySQL", "MS SQL", "MongoDB", "S3 / GCS"];

const EMPTY = {
  name: "", email: "", company: "", role: "", teamSize: "",
  warehouses: [], useCase: "", notes: "",
};

function Field({ id, label, children, optional, full }) {
  return (
    <div className={full ? "sm:col-span-2" : "sm:col-span-1"}>
      <Label htmlFor={id} className="font-mono text-xs uppercase tracking-[0.18em]">
        {label}{optional && <span className="text-muted-foreground/70"> (optional)</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function Demo() {
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [enriched, setEnriched] = useState(null);
  const [error, setError] = useState("");

  const multiStep = VERSION !== "v1";

  // Autosave (v2/v3) — restore on mount, persist on change.
  useEffect(() => {
    if (!multiStep) return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
      if (saved) setForm((f) => ({ ...f, ...saved }));
    } catch { /* ignore */ }
  }, [multiStep]);

  useEffect(() => {
    if (multiStep) localStorage.setItem(STORE_KEY, JSON.stringify(form));
  }, [form, multiStep]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (k) => (e) => set(k, e?.target?.value ?? e);

  const toggleWarehouse = (w) =>
    setForm((f) => ({
      ...f,
      warehouses: f.warehouses.includes(w)
        ? f.warehouses.filter((x) => x !== w)
        : [...f.warehouses, w],
    }));

  const onEmailBlur = async () => {
    if (VERSION !== "v3" || !form.email || !form.email.includes("@")) return;
    try {
      const { data } = await axios.post(`${API}/enrich`, { email: form.email });
      setEnriched(data);
      if (data.company && !form.company) set("company", data.company);
    } catch { /* enrichment is best-effort */ }
  };

  const submit = async () => {
    if (pending) return;
    setError("");
    setPending(true);
    try {
      const { data } = await axios.post(`${API}/lead`, {
        email: form.email,
        name: form.name || null,
        company: form.company || null,
        role: form.role || null,
        team_size: form.teamSize || null,
        warehouses: form.warehouses,
        use_case: form.useCase || null,
        notes: form.notes || null,
        source: "demo",
        version: VERSION,
      });
      setSubmitted(data);
      localStorage.removeItem(STORE_KEY);
    } catch (err) {
      setError(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setPending(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (multiStep && step === 1) {
      setStep(2);
      return;
    }
    submit();
  };

  return (
    <div data-testid="demo-root" className="relative min-h-screen w-full bg-background text-foreground">
      <SeoHead
        title="Book a Demo — See Sema on Your Own Data"
        description="A 20-minute walkthrough of Sema's semantic layer: watch your schema become a governed graph and ask your own questions in plain English."
        keywords="sema demo, semantic layer demo, governed analytics walkthrough"
        path="/demo"
      />
      <div aria-hidden="true" className="sema-grid pointer-events-none absolute inset-0 opacity-30" />

      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <Link to="/" data-testid="demo-back-home" className="inline-flex items-center gap-2 font-mono text-sm">
          <ArrowLeft className="h-4 w-4" />
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
          <span className="font-semibold">Sema</span>
        </Link>
        <ThemeToggle />
      </header>

      <main className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 py-16 lg:grid-cols-12 lg:gap-16">
        <section className="lg:col-span-5">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
            20-minute walkthrough
          </p>
          <h1 data-testid="demo-title" className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Bring your questions. We'll bring the graph.
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
            A live walkthrough against a sample warehouse, then 10 minutes wired to your real schema. No slides.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "See the semantic graph build itself from your schema.",
              "Ask three of your own questions in plain English.",
              "Inspect the SQL, policy, and audit trail behind each answer.",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground/90">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" strokeWidth={2.2} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 inline-flex items-center gap-2 text-xs">
            <Link to="/signup" data-testid="demo-to-signup" className="font-mono uppercase tracking-[0.2em] text-accent hover:underline">
              or start a free sandbox →
            </Link>
          </p>
        </section>

        <section className="lg:col-span-7">
          <div className="rounded-2xl border border-border/60 bg-card/60 p-7 backdrop-blur">
            {submitted ? (
              <div data-testid="demo-success" className="flex flex-col items-start gap-3 py-6">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-accent/40 bg-accent/15 text-accent">
                  <Check className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h2 className="text-xl font-semibold text-foreground">Got it — talk soon.</h2>
                <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
                  We'll reach out at <span className="font-mono text-foreground">{form.email}</span> within one business day.
                </p>
                <Link to="/" className="mt-4">
                  <Button variant="outline" className="rounded-full border-border/80 font-mono text-xs uppercase tracking-[0.22em]">
                    Back to home
                  </Button>
                </Link>
              </div>
            ) : (
              <form data-testid="demo-form" onSubmit={onSubmit} className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {multiStep && (
                  <div className="sm:col-span-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="demo-step-indicator">
                    <span className={step === 1 ? "text-accent" : ""}>① Who you are</span>
                    <span className="opacity-40">—</span>
                    <span className={step === 2 ? "text-accent" : ""}>② Your stack</span>
                  </div>
                )}

                {/* STEP 1 */}
                {(!multiStep || step === 1) && (
                  <>
                    {multiStep && (
                      <Field id="name" label="Name">
                        <Input id="name" data-testid="demo-input-name" required value={form.name} onChange={onChange("name")} placeholder="Avery Lin" />
                      </Field>
                    )}
                    <Field id="email" label="Work email" full={!multiStep}>
                      <Input id="email" type="email" data-testid="demo-input-email" required value={form.email} onChange={onChange("email")} onBlur={onEmailBlur} placeholder="avery@northwind.com" />
                      {enriched?.company && (
                        <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-accent">enriched · {enriched.company}</p>
                      )}
                    </Field>
                    {multiStep && (
                      <Field id="company" label="Company">
                        <Input id="company" data-testid="demo-input-company" required value={form.company} onChange={onChange("company")} placeholder="Northwind" />
                      </Field>
                    )}
                  </>
                )}

                {/* STEP 2 */}
                {multiStep && step === 2 && (
                  <>
                    <Field id="role" label="Role">
                      <Select value={form.role} onValueChange={onChange("role")}>
                        <SelectTrigger id="role" data-testid="demo-input-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
                      </Select>
                    </Field>
                    <Field id="team-size" label="Team size">
                      <Select value={form.teamSize} onValueChange={onChange("teamSize")}>
                        <SelectTrigger id="team-size" data-testid="demo-input-team-size"><SelectValue placeholder="Select size" /></SelectTrigger>
                        <SelectContent>{TEAM_SIZES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                      </Select>
                    </Field>

                    {VERSION === "v3" && (
                      <>
                        <Field id="warehouses" label="Where's your data?" full>
                          <div className="flex flex-wrap gap-2">
                            {WAREHOUSES.map((w) => {
                              const on = form.warehouses.includes(w);
                              return (
                                <button
                                  key={w}
                                  type="button"
                                  data-testid={`demo-warehouse-${w.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                  onClick={() => toggleWarehouse(w)}
                                  className={`rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                                    on ? "border-accent bg-accent/15 text-accent" : "border-border/60 text-muted-foreground hover:text-foreground"
                                  }`}
                                >
                                  {w}
                                </button>
                              );
                            })}
                          </div>
                        </Field>
                        <Field id="use-case" label="Primary use case" full>
                          <Input id="use-case" data-testid="demo-input-use-case" value={form.useCase} onChange={onChange("useCase")} placeholder="Self-serve metrics for the GTM team" />
                        </Field>
                        <Field id="notes" label="Anything else?" optional full>
                          <Textarea id="notes" data-testid="demo-input-notes" rows={3} value={form.notes} onChange={onChange("notes")} placeholder="E.g. Why did enterprise churn spike in November?" />
                        </Field>
                      </>
                    )}
                  </>
                )}

                {error && <p data-testid="demo-error" className="sm:col-span-2 text-sm text-destructive">{error}</p>}

                <div className="sm:col-span-2 flex flex-col items-start gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                  {multiStep && step === 2 ? (
                    <button type="button" data-testid="demo-back-step" onClick={() => setStep(1)} className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
                      ← Back
                    </button>
                  ) : (
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">We reply within one business day.</p>
                  )}
                  <Button
                    type="submit"
                    data-testid={multiStep && step === 1 ? "demo-next" : "demo-submit"}
                    size="lg"
                    disabled={pending}
                    className="rounded-full bg-accent px-6 font-mono text-xs uppercase tracking-[0.22em] text-accent-foreground hover:bg-accent/90"
                  >
                    {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : (multiStep && step === 1 ? (<>Continue<ArrowRight className="ml-2 h-4 w-4" /></>) : "Request walkthrough")}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
